import { useMemo, useState } from "react";
import { cn } from "../lib/cn.js";

// Расписание с подпараметрами:
//   • Раз в день         — 04:00 UTC
//   • Раз в неделю       — выбираешь день недели (Пн–Вс), 04:00 UTC
//   • Раз в месяц        — выбираешь число (1–28) или «Последний день»
//   • Свой cron          — 5 полей вручную
//
// Cron в формате croner с поддержкой L (последний день месяца).

type Mode = "none" | "daily" | "weekly" | "monthly" | "custom";

const WEEK_DAYS: Array<{ key: number; label: string }> = [
  { key: 1, label: "Пн" },
  { key: 2, label: "Вт" },
  { key: 3, label: "Ср" },
  { key: 4, label: "Чт" },
  { key: 5, label: "Пт" },
  { key: 6, label: "Сб" },
  { key: 0, label: "Вс" },
];

interface Props {
  value: string | null;
  onChange: (cron: string | null) => void;
  disabled?: boolean;
}

interface ParsedMode {
  mode: Mode;
  weekDay?: number;
  monthDay?: number | "L";
}

function parseMode(value: string | null): ParsedMode {
  if (!value) return { mode: "none" };
  if (value === "0 4 * * *") return { mode: "daily" };
  let m = /^0 4 \* \* (\d)$/.exec(value);
  if (m && m[1]) return { mode: "weekly", weekDay: Number.parseInt(m[1], 10) };
  if (value === "0 4 L * *") return { mode: "monthly", monthDay: "L" };
  m = /^0 4 (\d{1,2}) \* \*$/.exec(value);
  if (m && m[1]) {
    const d = Number.parseInt(m[1], 10);
    if (d >= 1 && d <= 28) return { mode: "monthly", monthDay: d };
  }
  return { mode: "custom" };
}

function buildCron(mode: Mode, weekDay: number, monthDay: number | "L"): string | null {
  switch (mode) {
    case "none":
      return null;
    case "daily":
      return "0 4 * * *";
    case "weekly":
      return `0 4 * * ${weekDay}`;
    case "monthly":
      return `0 4 ${monthDay} * *`;
    case "custom":
      return null;
  }
}

export function CronPicker({ value, onChange, disabled }: Props) {
  const initial = useMemo(() => parseMode(value), [value]);
  const [mode, setMode] = useState<Mode>(initial.mode);
  const [weekDay, setWeekDay] = useState<number>(initial.weekDay ?? 1);
  const [monthDay, setMonthDay] = useState<number | "L">(initial.monthDay ?? 1);
  const [customCron, setCustomCron] = useState<string>(
    initial.mode === "custom" ? value ?? "" : "",
  );

  // НЕ авто-сохраняем при смене mode — иначе клик «Раз в неделю» тут же
  // фиксирует с дефолтным днём (Пн) и закрывает picker до того как юзер
  // успеет выбрать нужный день. Сохранение — явной кнопкой.
  const draft = mode === "custom" ? customCron.trim() || null : buildCron(mode, weekDay, monthDay);
  const dirty = draft !== value;

  return (
    <div className="space-y-2">
      <ModeButton
        active={mode === "daily"}
        disabled={disabled}
        onClick={() => setMode("daily")}
        title="Раз в день"
        hint="04:00 UTC"
      />
      <ModeButton
        active={mode === "weekly"}
        disabled={disabled}
        onClick={() => setMode("weekly")}
        title="Раз в неделю"
        hint={`${WEEK_DAYS.find((d) => d.key === weekDay)?.label ?? "Пн"}, 04:00 UTC`}
      />
      {mode === "weekly" && (
        <div className="grid grid-cols-7 gap-1 px-1">
          {WEEK_DAYS.map((d) => (
            <button
              key={d.key}
              type="button"
              disabled={disabled}
              onClick={() => setWeekDay(d.key)}
              className={cn(
                "rounded-md py-1.5 text-xs transition",
                weekDay === d.key
                  ? "bg-button text-button-text font-medium"
                  : "bg-bg hover:bg-bg-secondary",
              )}
            >
              {d.label}
            </button>
          ))}
        </div>
      )}
      <ModeButton
        active={mode === "monthly"}
        disabled={disabled}
        onClick={() => setMode("monthly")}
        title="Раз в месяц"
        hint={
          monthDay === "L"
            ? "Последний день, 04:00 UTC"
            : `${monthDay}-го числа, 04:00 UTC`
        }
      />
      {mode === "monthly" && (
        <div className="space-y-2 px-1">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setMonthDay("L")}
            className={cn(
              "w-full rounded-md py-1.5 text-xs transition",
              monthDay === "L"
                ? "bg-button text-button-text font-medium"
                : "bg-bg hover:bg-bg-secondary",
            )}
          >
            Последний день месяца
          </button>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <button
                key={d}
                type="button"
                disabled={disabled}
                onClick={() => setMonthDay(d)}
                className={cn(
                  "rounded-md py-1 text-xs transition",
                  monthDay === d
                    ? "bg-button text-button-text font-medium"
                    : "bg-bg hover:bg-bg-secondary",
                )}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
      <ModeButton
        active={mode === "custom"}
        disabled={disabled}
        onClick={() => setMode("custom")}
        title="Свой cron"
        hint="5 полей"
      />
      {mode === "custom" && (
        <div className="space-y-1">
          <input
            type="text"
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
            placeholder="0 4 * * *"
            className="w-full rounded-lg bg-bg px-3 py-2 font-mono text-sm outline-none"
          />
          <div className="text-xs text-hint">
            <code>минуты часы день месяц день_недели</code>. Поддерживается{" "}
            <code>L</code> (последний день месяца).
          </div>
        </div>
      )}

      <button
        type="button"
        disabled={disabled || !dirty}
        onClick={() => onChange(draft)}
        className={cn(
          "w-full rounded-xl py-2.5 text-sm font-medium transition",
          dirty
            ? "bg-button text-button-text"
            : "bg-bg-secondary text-hint cursor-not-allowed",
        )}
      >
        {dirty ? "Сохранить" : "Без изменений"}
      </button>
    </div>
  );
}

function ModeButton(props: {
  active: boolean;
  disabled?: boolean | undefined;
  onClick: () => void;
  title: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className={cn(
        "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition",
        props.active ? "bg-button text-button-text" : "bg-bg hover:bg-bg-secondary",
      )}
    >
      <span>{props.title}</span>
      <span className={cn("text-xs", props.active ? "opacity-90" : "text-hint")}>
        {props.hint}
      </span>
    </button>
  );
}
